import { useNavigate } from "react-router-dom";
import { PulseCard } from "../components/PulseCard";
import type { AttentionProjection, BoardItemProjection, CrewProjection } from "../domain";
import { crewSurfacePath } from "../lib/routes";
import { formatAge } from "../lib/time";
import { useRemote } from "../state/RemoteContext";

export function exactAttentionBoardItem(
  item: AttentionProjection,
  crew: CrewProjection,
): BoardItemProjection | undefined {
  const workItem = item.workItem;
  if (!workItem
      || item.objectType !== "board-item"
      || item.objectId !== workItem.cardId
      || crew.id !== workItem.crewId
      || crew.boardId !== workItem.boardId) return undefined;
  return crew.board.find((candidate) => candidate.crewId === workItem.crewId
    && candidate.boardId === workItem.boardId
    && candidate.cardId === workItem.cardId
    && candidate.goalId === workItem.goalId);
}

export function AttentionScreen() {
  const { fixture, setDialog, can, historyReset } = useRemote();
  const navigate = useNavigate();
  const ranked = [...fixture.attention].sort((left, right) => right.rank - left.rank);
  const needsYouItems = ranked.filter((item) => ["decision", "risk", "blocker", "command-failed"].includes(item.kind));
  const needsYou = needsYouItems.length;
  const needsYouCrews = new Set(needsYouItems.map((item) => item.crewId)).size;
  const running = fixture.crews.filter((crew) => crew.runState === "running").length;
  const atRisk = fixture.crews.filter((crew) => crew.health === "at-risk").length;
  const completed = fixture.crews.reduce((total, crew) => total + crew.since.completedCount, 0);
  const decisions = fixture.crews.reduce((total, crew) => total + crew.since.decisionCount, 0);
  const canReadEvidence = can("evidence.read");
  const scopeLabel = fixture.crews.length > 0
    ? `Across ${fixture.crews.length} Crew snapshot${fixture.crews.length === 1 ? "" : "s"}`
    : "No Crews in this session";
  const runningCopy = running === 0
    ? "No Crews are currently running."
    : `${running} Crew${running === 1 ? " is" : "s are"} running.`;

  const openItem = (item: AttentionProjection) => {
    const crew = fixture.crews.find((candidate) => candidate.id === item.crewId);
    if (!crew) return;
    if (item.kind === "decision") {
      setDialog({ type: "decision", crewId: item.crewId, decisionId: item.objectId });
      return;
    }
    const boardItem = exactAttentionBoardItem(item, crew);
    if (boardItem) {
      setDialog({ type: "board", crewId: item.crewId, itemId: boardItem.cardId });
      return;
    }
    if (["risk", "blocker", "command-failed"].includes(item.kind)) {
      navigate(crewSurfacePath(item.crewId, "board"));
    } else if (item.kind === "completed" && canReadEvidence) {
      setDialog({ type: "evidence", crewId: item.crewId, evidenceIds: crew.evidence.map((evidence) => evidence.id) });
    } else {
      navigate(crewSurfacePath(item.crewId));
    }
  };

  return (
    <div className="screen attention-screen">
      <header className="screen-heading">
        <span className="eyebrow">{scopeLabel} · short-burst brief</span>
        <h1>Your Crews</h1>
        <p>{needsYou === 0 ? `Nothing needs you. ${runningCopy}` : `${needsYou} item${needsYou === 1 ? " needs" : "s need"} you across ${needsYouCrews} Crew${needsYouCrews === 1 ? "" : "s"}. ${runningCopy}`}</p>
      </header>
      {historyReset && (
        <div className="history-reset-note" role="status">
          <strong>History checkpoint reset</strong>
          <span>Earlier retained updates were replaced by the latest trusted snapshot. Counts now start from each Crew&apos;s current checkpoint.</span>
        </div>
      )}
      <div className="stat-grid" aria-label="Crew summary">
        <div className="stat" data-tone="attention"><span>Needs you</span><strong>{needsYou}</strong><small>across {needsYouCrews} Crew{needsYouCrews === 1 ? "" : "s"}</small></div>
        <div className="stat" data-tone="live"><span>Running</span><strong>{running}</strong><small>{atRisk === 0 ? "No Crews at risk" : `${atRisk} Crew${atRisk === 1 ? "" : "s"} at risk`}</small></div>
      </div>
      <div className="since-summary">
        <span>Across Crew checkpoints</span>
        <strong>{completed} items finished · {decisions} decision{decisions === 1 ? "" : "s"} · {atRisk} Crew{atRisk === 1 ? "" : "s"} at risk</strong>
      </div>
      <section className="pulse-list" aria-labelledby="ranked-attention-title">
        <h2 id="ranked-attention-title" className="section-title">Ranked attention</h2>
        {ranked.length === 0 && <p className="empty-state">Nothing needs attention in the Crews shared with this session.</p>}
        {ranked.map((item) => {
          const crew = fixture.crews.find((candidate) => candidate.id === item.crewId);
          const boardItem = crew ? exactAttentionBoardItem(item, crew) : undefined;
          const isAcked = item.acknowledged;
          return (
            <PulseCard
              key={item.id}
              kind={item.kind}
              eyebrow={`${crew?.shortName ?? "Crew"} · ${item.kind}`}
              title={item.title}
              summary={item.summary}
              meta={`${item.actor} · ${formatAge(item.occurredAt)} · ${boardItem?.cardId ?? item.objectId}${isAcked ? " · acknowledged" : ""}`}
              progress={item.kind === "running" ? 68 : undefined}
              isNew={item.isNew}
            >
              <button type="button" className={item.kind === "decision" ? "attention-button" : "secondary-button"} onClick={() => openItem(item)}>
                {item.kind === "decision" ? "Review" : boardItem ? "Inspect card" : item.kind === "risk" ? "Inspect Board" : item.kind === "completed" && canReadEvidence ? "Evidence" : ["info", "blocker", "command-failed", "completed"].includes(item.kind) ? "Open Crew" : "Open Run"}
              </button>
            </PulseCard>
          );
        })}
      </section>
    </div>
  );
}
